import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";
import { submitQuote } from "../lib/api";

const inputCls =
  "w-full bg-white border border-vr-border rounded-lg px-4 py-2.5 text-vr-ink placeholder:text-vr-dim/60 focus:outline-none focus:border-vr-brand focus:ring-2 focus:ring-vr-brand/15 transition-all";

export const QuoteModal = ({ open, onClose, product }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitQuote({
        ...form,
        sku: product?.sku,
        product_name: product?.name,
      });
      toast.success("Quote request sent — Victor will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="quote-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-vr-navy/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
          >
            <div className="bg-vr-navy text-white px-6 py-5">
              <button data-testid="quote-close" onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors">
                <X size={22} />
              </button>
              <h3 className="font-display font-bold text-xl">Request a Quote</h3>
              {product && (
                <p className="mt-1 text-white/70 text-sm">
                  {product.name} <span className="font-mono text-vr-hygiene">· {product.sku}</span>
                </p>
              )}
            </div>

            <form onSubmit={submit} data-testid="quote-form" className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Name *</label>
                <input data-testid="quote-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Email *</label>
                <input data-testid="quote-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.co.nz" />
              </div>
              <div>
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Phone</label>
                <input data-testid="quote-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+64 ..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Company</label>
                <input data-testid="quote-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Quantity needed</label>
                <input data-testid="quote-quantity" className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 10 cartons / per month" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-vr-ink mb-1.5">Message</label>
                <textarea data-testid="quote-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Any details we should know…" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" data-testid="quote-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={17} />}
                  {loading ? "Sending…" : "Send Quote Request"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
